/**
 * WHICH CHANNEL a message to a passenger goes out on, and which saved
 * messages belong to it.
 *
 * TWO CHANNELS, AND THEY ARE NOT THE SAME KIND OF MESSAGE. A WhatsApp goes
 * out inside the approved `we_have_a_message_for_you` template, so its body
 * is a template VARIABLE: one line, capped, with a greeting already written
 * above it (Utils/passengerTemplate holds that). An email has a subject, has
 * no line constraint and no template at all. A composer therefore changes
 * SHAPE between them rather than changing a label, and a saved message
 * written for one is not sendable on the other.
 *
 * WHY THIS IS ITS OWN MODULE. Five places ask the same two questions — the
 * portal's dispatcher composer, the dashboard's composer, the dashboard's
 * message triggers, the Saved Messages list and its form. The rule that a
 * channel-less saved message is a WhatsApp one is a DATA rule about rows
 * that already exist in production, and five copies of it would be five
 * chances for one of them to start showing an email snippet in a WhatsApp
 * composer.
 *
 * THE ABSENT CHANNEL IS WHATSAPP, deliberately and not as a convenience.
 * Saved messages predate this field: the rows in production carry no
 * `channel` at all and every one of them was written for the WhatsApp
 * template. The API says the same thing from its side ("a preset with no
 * `channel` is a WhatsApp one"), so this is the browser agreeing with the
 * server rather than guessing.
 */

export const WHATSAPP = 'whatsapp';
export const EMAIL = 'email';

/** Is this the email channel? Anything else — including nothing — is WhatsApp. */
export const isEmailChannel = (channel) => channel === EMAIL;

/** The channel a value names, normalised. Absent or unknown → WhatsApp. */
export const messageChannel = (channel) => (isEmailChannel(channel) ? EMAIL : WHATSAPP);

/**
 * The channel one saved message belongs to.
 *
 * Reads the row rather than the composer: an old row with no `channel` is a
 * WhatsApp one, which is the whole reason this is a function and not an
 * equality test at each call site.
 */
export const presetChannel = (preset) => messageChannel(preset?.channel);

/**
 * The saved messages for one channel, in the order they arrived.
 *
 * The API can also filter server-side (`GET /message-presets?channel=…`),
 * but the composers filter HERE on purpose: a channel toggle must swap the
 * chip row instantly and without a second round trip, and the payload
 * already carries each row's channel.
 */
export const presetsForChannel = (presets, channel) => {
  const wanted = messageChannel(channel);
  return (presets || []).filter((preset) => presetChannel(preset) === wanted);
};

/**
 * The subject an email composer opens with — editable, never imposed.
 *
 * It names the booking, because a passenger with a transfer booked months
 * ago reads the subject line in a crowded inbox and has to recognise it
 * before they open anything. The reference is the one thing they can match
 * against their own confirmation.
 *
 * Deliberately NOT capped to some invented length: the API has not published
 * a subject limit, and a number made up here would be a claim about a rule
 * that does not exist.
 */
export const defaultEmailSubject = (reference) => (
  reference ? `Your booking ${reference}` : 'Your booking with Pickup Hungary'
);

export default messageChannel;
